import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { usePlatform } from '../context/PlatformContext'
import Sidebar from './Sidebar'
import BottomNav from './BottomNav'
import OfflineBanner from './OfflineBanner'
import UpdateBanner from './UpdateBanner'
import ImpersonationBanner from './ImpersonationBanner'
import {
  ChatBubbleOvalLeftEllipsisIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowRightStartOnRectangleIcon
} from '@heroicons/react/24/outline'

// Check if we're in dev environment
const isDev = import.meta.env.VITE_ENVIRONMENT === 'dev'

function Layout({ children }) {
  const { getAccessToken, authMethod, logout, user } = useAuth()
  const { isPlatformAdmin, selectedTenantId } = usePlatform()
  const location = useLocation()
  const hasPlatformBanner = isPlatformAdmin && !!selectedTenantId
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackSending, setFeedbackSending] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState(null)

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed)
  }, [sidebarCollapsed])

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return
    setFeedbackSending(true)
    try {
      const token = getAccessToken()
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: feedbackText, pageUrl: location.pathname })
      })
      const data = await res.json()
      if (data.success) {
        setFeedbackMessage({ type: 'success', text: 'Thank you for your feedback!' })
        setFeedbackText('')
        setTimeout(() => { setShowFeedback(false); setFeedbackMessage(null) }, 2000)
      } else {
        setFeedbackMessage({ type: 'error', text: data.error || 'Failed to submit' })
      }
    } catch (error) {
      setFeedbackMessage({ type: 'error', text: 'Failed to submit feedback' })
    } finally {
      setFeedbackSending(false)
    }
  }

  const handleShowFeedback = () => setShowFeedback(true)

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col relative isolate">
      {/* Faint brand-coloured light behind everything. -z-10 inside the
          isolated root: above the root's own background, below the content. */}
      <div aria-hidden="true" className="app-ambient fixed inset-0 -z-10 pointer-events-none" />

      {/* Fixed status bar background for iOS. Frosted rather than solid: content
          scrolling under the Dynamic Island is blurred out instead of hidden. */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass safe-status-bar" />

      {/* Platform Admin Banner - fixed at very top, above everything */}
      <ImpersonationBanner />

      {/* Dev Environment Banner - below platform banner if present */}
      {isDev && (
        <div className={`bg-amber-500 text-slate-900 text-center py-1.5 px-4 text-sm font-semibold fixed left-0 right-0 z-50 ${hasPlatformBanner ? 'top-9' : 'top-0'}`}>
          ⚠️ DEVELOPMENT ENVIRONMENT — Changes here do not affect production
        </div>
      )}

      <div className="flex-1 flex">
        {/* Sidebar - desktop only */}
        <Sidebar
          hasBanner={isDev}
          hasPlatformBanner={hasPlatformBanner}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onShowFeedback={handleShowFeedback}
        />

        {/* Bottom nav - mobile only */}
        <BottomNav onShowFeedback={handleShowFeedback} />

        {/* Main content */}
        <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <UpdateBanner />
          <OfflineBanner />
          <main className={`flex-1 px-4 lg:px-6 overflow-x-hidden safe-content-bottom lg:pb-6 ${isDev ? 'pt-12 lg:pt-12' : 'safe-content-top lg:pt-4'}`}>
            {children}
          </main>
        </div>
      </div>

      {/* Floating logout button for PIN-authenticated sessions */}
      {authMethod === 'pin' && (
        <button
          onClick={logout}
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50 flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg shadow-red-900/30 transition-colors"
        >
          <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
          <span className="text-sm font-medium">Sign Out{user?.name ? ` (${user.name.split(' ')[0]})` : ''}</span>
        </button>
      )}

      {/* Feedback modal - shared by Sidebar and BottomNav */}
      {showFeedback && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => { setShowFeedback(false); setFeedbackMessage(null) }}>
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ChatBubbleOvalLeftEllipsisIcon className="w-5 h-5 text-amber-500" />
                Provide Feedback
              </h3>
              <button onClick={() => { setShowFeedback(false); setFeedbackMessage(null) }} className="text-slate-400 hover:text-white">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Share your thoughts, report bugs, or suggest improvements.
            </p>
            {feedbackMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                feedbackMessage.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}>
                {feedbackMessage.text}
              </div>
            )}
            <textarea
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              placeholder="What's on your mind?"
              rows={4}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 resize-none focus:outline-none focus:border-amber-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setShowFeedback(false); setFeedbackMessage(null) }}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitFeedback}
                disabled={feedbackSending || !feedbackText.trim()}
                className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {feedbackSending ? 'Sending...' : <><PaperAirplaneIcon className="w-4 h-4" /> Send</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Layout
